import apiClient from '../api/api';

export interface UserInfo {
  iss: string;
  exp: number;
  iat: number;
  auth_time: number;
  jti: string;
  sid: string;
  sub: string;
  email: string;
  name: string;
}

export interface DeviceInfo {
  ua: string;
  browser: {
    name: string;
    version: string;
    major: string;
  };
  engine: {
    name: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
  };
  cpu: {
    architecture: string;
  };
}

export interface AuditTrailData {
  old_data?: any;
  new_data?: any;
  [key: string]: any;
}

export interface AuditTrailReport {
  _id: string;
  is_deleted: boolean;
  user_id: string;
  event_type: string;
  event_time: string;
  event_description: string;
  action_by: string;
  user_info?: UserInfo;
  data?: AuditTrailData;
  ip_address: string;
  device_info: DeviceInfo;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface AuditTrailListParams {
  skip: number;
  limit: number;
  user_id?: string;
  event_type?: string;
  action_by?: string;
  start_date?: string;
  end_date?: string;
  email?: string;
}

export interface AuditTrailListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    audit_trail_reports: AuditTrailReport[];
  };
}

export interface EventType {
  value: string;
  label: string;
}

export interface AuditTrailStats {
  total_events: number;
  today_events: number;
  unique_users: number;
  most_common_event: string;
}

class AuditTrailService {
  // GET: Fetch audit trail reports with pagination and filters
  async getAuditTrailReports(params: AuditTrailListParams): Promise<AuditTrailListResponse> {
    try {
      const response = await apiClient.post('/dms-v2-srv/audit-trail-report-srv/utils/list', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit trail reports:', error);
      throw error;
    }
  }

  // GET: Fetch event types for dropdown
  async getEventTypes(): Promise<EventType[]> {
    try {
      const response = await apiClient.get('/dms-v2-srv/audit-trail-report-srv/utils/event-types');

      // Handle the response structure: { success: true, code: 200, data: [...] }
      const eventTypes = response.data?.data || response.data;

       if (Array.isArray(eventTypes)) {
        return eventTypes
        .filter((eventType: string) => eventType !== 'USER_GROUP_MAP_UPDATED') // Exclude USER_GROUP_MAP_UPDATED
        .map((eventType: string) => ({
          value: eventType,
          label: this.formatEventTypeLabel(eventType)
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching event types:', error);
      return [];
    }
  }

  // Helper function to format event type labels
  private formatEventTypeLabel(eventType: string): string {
    // Handle specific cases for better readability
    const specialCases: Record<string, string> = {
      'USER_LOGIN': 'User Login',
      'USER_LOGOUT': 'User Logout',
      'SOCIAL_LOGIN': 'Social Login',
      'USER_REGISTRATION_VIA_SOCIAL': 'Social Registration',
      'SETTINGS_AI_CONFIG_CREATED': 'AI Config Created',
      'SETTINGS_AI_CONFIG_UPDATED': 'AI Config Updated',
      'SETTINGS_AI_CONFIG_DELETED': 'AI Config Deleted',
      'SETTINGS_VECTOR_STORE_CONFIG_CREATED': 'Vector Store Config Created',
      'SETTINGS_VECTOR_STORE_CONFIG_UPDATED': 'Vector Store Config Updated',
      'SETTINGS_VECTOR_STORE_CONFIG_DELETED': 'Vector Store Config Deleted',
      'SETTINGS_FILE_STORAGE_CONFIG_CREATED': 'File Storage Config Created',
      'SETTINGS_FILE_STORAGE_CONFIG_UPDATED': 'File Storage Config Updated',
      'SETTINGS_FILE_STORAGE_CONFIG_DELETED': 'File Storage Config Deleted',
      'FILE_UPLOADED_AT_APP_VIA_NAMESPACE': 'File Uploaded via Namespace',
      'FILE_DELETED_AT_APP_VIA_NAMESPACE': 'File Deleted via Namespace',
      'NAMESPACE_CREATED_AT_APP': 'Namespace Created',
      'NAMESPACE_UPDATED_AT_APP': 'Namespace Updated',
      'NAMESPACE_DELETED_AT_APP': 'Namespace Deleted',
    };

    if (specialCases[eventType]) {
      return specialCases[eventType];
    }

    // Generic formatting: replace underscores with spaces and title case
    return eventType
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/\bVia\b/g, 'via') // Keep 'via' lowercase
      .replace(/\bAt\b/g, 'at')   // Keep 'at' lowercase
      .replace(/\bConfig\b/g, 'Configuration'); // Expand 'Config' to 'Configuration'
  }

  // GET: Generate audit trail statistics
  async getAuditTrailStats(): Promise<AuditTrailStats> {
    try {
      // Get all records to calculate stats (could be optimized with dedicated endpoint)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const [allReports, todayReports] = await Promise.all([
        this.getAuditTrailReports({ skip: 0, limit: 1000 }),
        this.getAuditTrailReports({
          skip: 0,
          limit: 1000,
          start_date: today.toISOString(),
          end_date: tomorrow.toISOString()
        })
      ]);

      //  Fetch Live User Count from User Service
      let liveUniqueUserCount = 0;
      try {
        const userResponse = await apiClient.post('/dms-v2-srv/user-srv/utils/list', { 
            skip: 0, 
            limit: 1 
        });
        liveUniqueUserCount = userResponse.data?.data?.totalUsers || 0;
      } catch (userError) {
        console.error('Error fetching live user count:', userError);
        const reports = allReports.data.audit_trail_reports;
        liveUniqueUserCount = new Set(reports.map(r => r.user_id || r.action_by).filter(Boolean)).size;
      }

      const reports = allReports.data.audit_trail_reports;
      const eventCounts = reports.reduce((acc, report) => {
        acc[report.event_type] = (acc[report.event_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const mostCommonEvent = Object.entries(eventCounts).reduce(
        (max, [event, count]) => count > max.count ? { event, count } : max,
        { event: 'N/A', count: 0 }
      ).event;

      return {
        total_events: allReports.data.total_count,
        today_events: todayReports.data.total_count,
        unique_users: liveUniqueUserCount, 
        most_common_event: mostCommonEvent.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      };
    } catch (error) {
      console.error('Error fetching audit trail stats:', error);
      return {
        total_events: 0,
        today_events: 0,
        unique_users: 0,
        most_common_event: 'N/A'
      };
    }
  }

  // Helper function to export audit trail data (could be enhanced with backend support)
  exportAuditTrailData(reports: AuditTrailReport[], format: 'csv' | 'json' = 'csv') {
    if (format === 'json') {
      const dataStr = JSON.stringify(reports, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `audit_trail_${new Date().toISOString().split('T')[0]}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } else {
      // CSV export
      const headers = ['Event Time', 'Event Type', 'User Email', 'Description', 'IP Address', 'Browser', 'OS'];
      const csvContent = [
        headers.join(','),
        ...reports.map(report => [
          new Date(report.event_time).toLocaleString(),
          report.event_type,
          report.user_info?.email || 'N/A',
          `"${report.event_description}"`,
          report.ip_address || 'N/A',
          `${report.device_info?.browser?.name || 'Unknown'} ${report.device_info?.browser?.version || ''}`.trim(),
          `${report.device_info?.os?.name || 'Unknown'} ${report.device_info?.os?.version || ''}`.trim()
        ].join(','))
      ].join('\n');

      const dataUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
      const exportFileDefaultName = `audit_trail_${new Date().toISOString().split('T')[0]}.csv`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }
  }
}

export const auditTrailService = new AuditTrailService();
export default auditTrailService;